import { useEffect, useRef, useState } from "react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MessageCircle, Send } from "lucide-react";

export default function ChatPanel({ trigger }) {
  const [open, setOpen] = useState(false);
  const [peers, setPeers] = useState([]);
  const [activePeer, setActivePeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [totalUnread, setTotalUnread] = useState(0);
  const pollRef = useRef(null);
  const endRef = useRef(null);

  const loadPeers = async () => {
    try {
      const { data } = await api.get("/messages/peers");
      setPeers(data);
      setTotalUnread(data.reduce((s, p) => s + (p.unread || 0), 0));
    } catch (_) { /* ignore */ }
  };

  const loadMessages = async (peerId) => {
    if (!peerId) return;
    try {
      const { data } = await api.get(`/messages?peer_id=${peerId}`);
      setMessages(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    loadPeers();
    const t = setInterval(loadPeers, 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open || !activePeer) return;
    loadMessages(activePeer.id);
    pollRef.current = setInterval(() => loadMessages(activePeer.id), 3000);
    return () => clearInterval(pollRef.current);
  }, [open, activePeer]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activePeer) return;
    try {
      await api.post("/messages", {
        to_id: activePeer.id,
        to_role: activePeer.role,
        content: text.trim(),
      });
      setText("");
      loadMessages(activePeer.id);
    } catch (_) { /* ignore */ }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" className="relative gap-2" data-testid="open-chat-btn">
            <MessageCircle className="w-4 h-4" /> Messages
            {totalUnread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 text-xs rounded-full bg-emerald-500 text-white flex items-center justify-center" data-testid="chat-unread-badge">
                {totalUnread}
              </span>
            )}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-5 py-4 border-b">
          <SheetTitle>Messages</SheetTitle>
        </SheetHeader>

        {!activePeer ? (
          <div className="flex-1 overflow-y-auto">
            {peers.length === 0 && (
              <p className="text-sm text-slate-500 p-6">No contacts yet.</p>
            )}
            {peers.map((p) => (
              <button
                key={p.id}
                className="w-full text-left p-4 hover:bg-slate-50 border-b flex items-center gap-3"
                onClick={() => setActivePeer(p)}
                data-testid={`peer-${p.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center font-semibold text-slate-700">
                  {p.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500 truncate">{p.subtitle}</div>
                </div>
                {p.unread > 0 && (
                  <span className="w-5 h-5 text-xs rounded-full bg-emerald-500 text-white flex items-center justify-center">{p.unread}</span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b flex items-center gap-3">
              <button onClick={() => setActivePeer(null)} className="text-sky-600 text-sm" data-testid="chat-back">← Back</button>
              <div className="font-medium text-slate-900">{activePeer.name}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
              {messages.map((m) => {
                const mine = m.from_id !== activePeer.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-sky-500 text-white" : "bg-white border"}`}>
                      {m.content}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="border-t p-3 flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." data-testid="chat-input" />
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="chat-send">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
